import { Collapse, FormInstance, Input, message, Select, Spin, Typography } from 'antd';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useQueryClient } from 'react-query';
import { useOutletContext, useSearchParams } from 'react-router-dom';

import useAPIMutation from '../../api/useAPIMutation';
import useAPIQuery from '../../api/useAPIQuery';
import useInfiniteAPIQuery from '../../api/useInfiniteAPIQuery';
import ErrorResult from '../../components/ErrorResult';
import Form from '../../components/Form';
import RichEditor from '../../components/RichEditor';
import useDiscardChangesModal from '../../hooks/useDiscardChangesModal';
import usePrefetchPages from '../../hooks/usePrefetchPages';
import useTypedNavigate from '../../hooks/useTypedNavigate';
import useTypedParams from '../../hooks/useTypedParams';
import { JobPostingId, CandidateId } from '../../types';
import { InterviewDetailSerializer } from '../../types/api';
import styles from './EditCandidateInterviewPage.module.css';

interface EditCandidateInterviewFormValues {
  interviewInfos: {
    stageId: number;
    interviewScript: number | null;
    interviewOpinionStatus: InterviewDetailSerializer['opinion_status'];
    interviewOpinionJustification?: string;
    interviewAnnotations?: string;
  }[];
}

const EditCandidateInterviewPage = () => {
  const { tenant, applicant_id, id } = useTypedParams({
    id: JobPostingId,
    applicant_id: CandidateId,
    tenant: String,
  });

  const [form] = Form.useForm();
  const [setIsDirty] = useDiscardChangesModal(form);

  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const navigate = useTypedNavigate();

  const { setDrawerForm: setForm, setDrawerMutationIsLoading: setMutationIsLoading } =
    useOutletContext<{
      setDrawerForm: React.Dispatch<React.SetStateAction<FormInstance | undefined>>;
      setDrawerMutationIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
    }>();

  const candidate = useAPIQuery(`/candidates/${applicant_id}/`);

  const recruitmentProcess = useAPIQuery(
    ['/recruitment-processes/', { params: { candidate: applicant_id, job_posting: id } }],
    {
      select: (data) => {
        return data.results[0];
      },
    }
  );

  const interviewScriptTemplatePages = useInfiniteAPIQuery(['/interview-script-templates/']);

  const interviewScriptTemplates = usePrefetchPages(
    interviewScriptTemplatePages,
    (data) => data.results
  );

  const interviewPages = useInfiniteAPIQuery([
    '/interviews/',
    {
      params: {
        recruitment_process: recruitmentProcess.data?.id,
      },
    },
  ]);

  const interviews = usePrefetchPages(interviewPages, (data) => data.results);

  const stagePages = useInfiniteAPIQuery([
    '/stages/',
    {
      params: {
        job_posting_id: id,
        sub_type__in:
          'SCREENING_INTERVIEW,HR_INTERVIEW,MANAGER_INTERVIEW,STAKEHOLDERS_INTERVIEW,PRACTICAL_TECHNICAL_CASE',
        expand: 'interview_script_content',
      },
    },
  ]);

  const stages = usePrefetchPages(stagePages, (data) => data.results);

  const mutation = useAPIMutation(
    async ({ interviewInfos }: EditCandidateInterviewFormValues, api) => {
      for (const interviewInfo of interviewInfos) {
        const stage = stages.find((stage) => stage.id === interviewInfo.stageId);

        if (stage) {
          const interview = interviews.find((interview) => interview.stage === stage.id);

          if (!stage.lock_interview_script) {
            await api.patch(`/stages/${stage.id}/`, {
              interview_script_template: interviewInfo.interviewScript || null,
            });
          }

          if (interview) {
            await api.patch(`/interviews/${interview.id}/`, {
              annotations: interviewInfo.interviewAnnotations || '',
              ...(interview.status === 'FINISHED'
                ? {
                    opinion_status:
                      interviewInfo.interviewOpinionStatus !== 'PENDING'
                        ? interviewInfo.interviewOpinionStatus
                        : undefined,
                    opinion_justification: interviewInfo.interviewOpinionJustification || '',
                  }
                : {}),
            });
          }
        }
      }
    },
    {
      onSettled: async () => {
        await queryClient.invalidateQueries('/stages/');
        await queryClient.invalidateQueries('/interviews/');
      },
      onSuccess: () => {
        setIsDirty(false);
        message.success('Candidato atualizado!');
        if (id) {
          navigate({
            pathname: '/:tenant/opportunities/:id/analysis/candidates/:applicant_id/interviews/',
            params: { tenant, id, applicant_id },
            search: searchParams,
          });
        }
      },
    }
  );

  useEffect(() => {
    setForm(form);
  }, [form, setForm]);

  useEffect(() => {
    setMutationIsLoading(mutation.isLoading);
  }, [mutation.isLoading, setMutationIsLoading]);

  return (
    <ErrorResult
      error={
        recruitmentProcess.error ||
        stagePages.error ||
        interviewPages.error ||
        interviewScriptTemplatePages.error
      }
    >
      <Spin
        spinning={
          recruitmentProcess.isLoading ||
          stagePages.isLoading ||
          interviewPages.isLoading ||
          interviewScriptTemplatePages.isLoading
        }
      >
        <Helmet>
          <title>
            Editar Entrevistas | {candidate.data?.name || '...'} | Recrutamento e Seleção
          </title>
        </Helmet>
        <Form<EditCandidateInterviewFormValues>
          form={form}
          layout="vertical"
          initialValues={{
            interviewInfos: stages.map((stage) => {
              const interview = interviews.find((interview) => interview.stage === stage.id);
              return {
                stageId: stage.id,
                interviewScript:
                  !stage.interview_script_template && stage.interview_script_content
                    ? 0
                    : stage.interview_script_template,
                interviewOpinionStatus: interview?.opinion_status || null,
                interviewOpinionJustification: interview?.opinion_justification || '',
                interviewAnnotations: interview?.annotations || '',
              };
            }),
          }}
          onFinish={(values) => {
            mutation.mutate(values);
          }}
          onValuesChange={() => setIsDirty(form.isFieldsTouched())}
        >
          <Form.List name="interviewInfos">
            {(fields) => (
              <>
                {fields.map(({ key, name }) => {
                  const currentInterview = interviews.find(
                    (interview) => interview.stage === stages[name].id
                  );

                  return (
                    <Collapse ghost expandIconPosition="end" className={styles.collapse}>
                      <Collapse.Panel
                        key={key}
                        header={
                          <Typography.Title level={4}>
                            {stages[name].name || '...'}
                          </Typography.Title>
                        }
                      >
                        <Form.Item name={[name, 'stageId']} hidden>
                          <Input />
                        </Form.Item>
                        <Form.Row gutter={[16, 16]}>
                          <Form.Col span={12}>
                            {stages[name].lock_interview_script ? (
                              <>
                                <Form.Item label="Roteiro da entrevista">
                                  <Select
                                    disabled
                                    value={stages[name].interview_script_name}
                                    options={[
                                      {
                                        key: stages[name].interview_script_name,
                                        value: stages[name].interview_script_name,
                                        label: stages[name].interview_script_name,
                                      },
                                    ]}
                                  />
                                </Form.Item>
                              </>
                            ) : (
                              <>
                                <Form.Item
                                  name={[name, 'interviewScript']}
                                  label="Roteiro da entrevista"
                                >
                                  <Select
                                    placeholder="Escolha o roteiro"
                                    style={{ width: '100%' }}
                                    options={[
                                      ...(stages[name].interview_script_template === null &&
                                      (stages[name].interview_script_content || '').length > 0
                                        ? [
                                            {
                                              key: `personalizado`,
                                              value: 0,
                                              label: 'Personalizado',
                                            },
                                          ]
                                        : []),
                                      ...interviewScriptTemplates
                                        .filter((template) =>
                                          template.type.includes(
                                            stages[name].sub_type as
                                              | 'SCREENING_INTERVIEW'
                                              | 'HR_INTERVIEW'
                                              | 'MANAGER_INTERVIEW'
                                              | 'STAKEHOLDERS_INTERVIEW'
                                              | 'PRACTICAL_TECHNICAL_CASE'
                                          )
                                        )
                                        .map((interviewScriptTemplate) => ({
                                          key: `${interviewScriptTemplate.id}-${interviewScriptTemplate.name}`,
                                          value: interviewScriptTemplate.id,
                                          label: interviewScriptTemplate.name,
                                        })),
                                    ]}
                                  />
                                </Form.Item>
                              </>
                            )}
                          </Form.Col>
                          <Form.Col span={12}>
                            <Form.Item
                              name={[name, 'interviewOpinionStatus']}
                              label="Parecer da entrevista"
                            >
                              <Select
                                disabled={currentInterview?.status !== 'FINISHED'}
                                options={[
                                  {
                                    value: 'QUALIFIED',
                                    key: 'QUALIDIED',
                                    label: 'Qualificado',
                                  },
                                  {
                                    value: 'NOT_QUALIFIED',
                                    key: 'NOT_QUALIFIED',
                                    label: 'Não qualificado',
                                  },
                                  {
                                    value: 'IN_ANALYSIS',
                                    key: 'IN_ANALYSIS',
                                    label: 'Em análise',
                                  },
                                  ...(currentInterview?.opinion_status === 'PENDING'
                                    ? [
                                        {
                                          value: 'PENDING',
                                          key: 'PENDING',
                                          label: 'Pendente',
                                        },
                                      ]
                                    : []),
                                ]}
                              />
                            </Form.Item>
                          </Form.Col>
                        </Form.Row>
                        <Form.Row gutter={[16, 16]}>
                          <Form.Col flex={1}>
                            <Form.Item
                              name={[name, 'interviewOpinionJustification']}
                              label="Justificativa do parecer"
                              rules={[{ required: currentInterview?.status === 'FINISHED' }]}
                            >
                              <Input.TextArea
                                disabled={currentInterview?.status !== 'FINISHED'}
                                rows={currentInterview?.status === 'FINISHED' ? 15 : 3}
                              />
                            </Form.Item>
                          </Form.Col>
                        </Form.Row>
                        <Form.Row gutter={[16, 16]}>
                          <Form.Col flex={1}>
                            {currentInterview?.id ? (
                              <Form.Item name={[name, 'interviewAnnotations']} label="Anotações">
                                <RichEditor image={false} video={false} />
                              </Form.Item>
                            ) : (
                              <Form.Item label="Anotações">
                                <Input.TextArea disabled rows={3} />
                              </Form.Item>
                            )}
                          </Form.Col>
                        </Form.Row>
                      </Collapse.Panel>
                    </Collapse>
                  );
                })}
              </>
            )}
          </Form.List>
        </Form>
      </Spin>
    </ErrorResult>
  );
};

export default EditCandidateInterviewPage;
